import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid, parse, parseISO, startOfMonth } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { useEsgDashboardStore } from '../../stores/esg-dashboard.store';
import { useCarbonAvoidanceReportStore } from '../../stores/carbon-avoidance-report.store';
import { ReportPdfTemplate, type ReportPdfVariant } from '../../components/report/ReportPdfTemplate';
import {
  CarbonAvoidanceReportPdfTemplate,
  carbonReportPeriodLabelFromReport,
} from '../../components/report/CarbonAvoidanceReportPdfTemplate';
import { ReportDownloadPdfButton } from '../../components/report/ReportDownloadPdfButton';
import { ReportMetricDefinitionsTab } from '../../components/report/ReportMetricDefinitionsTab';
import EsgReportCustomizeDialog from '../../components/report/EsgReportCustomizeDialog';
import {
  ALL_ESG_REPORT_SECTIONS,
  type EsgReportSectionId,
} from '../../constants/esgReportCustomize.catalog';
import {
  loadEsgReportPreferences,
  saveEsgReportPreferences,
} from '../../utils/esgReportPreferences.helpers';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import {
  PAGE_HEADER_TABS_ROW_SX,
  PAGE_HEADER_TABS_SX,
} from '../../constants/page-header';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import {
  HELP_GUIDE_METRIC_DEFINITIONS,
  parseHelpDownloadReportGuide,
  type HelpDownloadReportTarget,
} from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../../utils/helpGuideSession';
import {
  carbonReportPeriodToParams,
  isCarbonReportPeriodValid,
  readCarbonReportPeriodFromSession,
} from '../../utils/carbonAvoidance.util';
import { downloadReportPdf, sanitizeReportFileBaseName } from '../../utils/downloadReportPdf';
import { fetchImageAsDataUrl } from '../../utils/reportImage.helpers';
import '../../styles/report-print.css';

function parseDashboardMonth(iso: string): Date {
  if (!iso) return startOfMonth(new Date());
  if (/^\d{4}-\d{2}$/.test(iso)) {
    const d = parse(`${iso}-01`, 'yyyy-MM-dd', new Date());
    return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
  }
  const d = parseISO(iso);
  return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
}

const Reports: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { isStep, completeStep, dismissTour, setTourPhase, tourPhase } =
    useSelfSetupTour();
  const showReportsTour = isStep('esgReports');
  const [helpDownloadTarget, setHelpDownloadTarget] =
    useState<HelpDownloadReportTarget | null>(null);
  const [helpMetricDefsGuide, setHelpMetricDefsGuide] = useState(false);
  type ReportsTourPhase =
    | 'period'
    | 'download'
    | 'metricDefsTab'
    | 'metricSearch'
    | 'metricCards';
  type HelpReportPhase = 'period' | 'download';
  const [reportsTourPhase, setReportsTourPhase] =
    useState<ReportsTourPhase>('period');
  const [helpReportPhase, setHelpReportPhase] =
    useState<HelpReportPhase>('period');
  const helpDownloadGuide = helpDownloadTarget != null;
  const showReportsPageTour = showReportsTour && tourPhase !== 'nav';
  const activeReportsGuide =
    showReportsPageTour || helpDownloadGuide || helpMetricDefsGuide;
  const showPeriodTip =
    (showReportsPageTour && reportsTourPhase === 'period' && !helpDownloadGuide) ||
    (helpDownloadGuide && helpReportPhase === 'period');
  const showTourDownloadTip =
    showReportsPageTour && reportsTourPhase === 'download' && !helpDownloadGuide;
  const showHelpDownloadTip =
    helpDownloadGuide && helpReportPhase === 'download';
  const showHelpAllDownloadTip =
    showHelpDownloadTip && helpDownloadTarget === 'all';
  const showGridDownloadTip = showTourDownloadTip || showHelpAllDownloadTip;
  const showHelpDownloadGeneral =
    showHelpDownloadTip && helpDownloadTarget === 'general';
  const showHelpDownloadWaste =
    showHelpDownloadTip && helpDownloadTarget === 'waste';
  const showHelpDownloadCarbon =
    showHelpDownloadTip && helpDownloadTarget === 'carbon';
  const showMetricDefsTabTip =
    (showReportsPageTour || helpMetricDefsGuide) &&
    reportsTourPhase === 'metricDefsTab';
  const showMetricSearchTip =
    (showReportsPageTour || helpMetricDefsGuide) &&
    reportsTourPhase === 'metricSearch';
  const showMetricCardsTip =
    (showReportsPageTour || helpMetricDefsGuide) &&
    reportsTourPhase === 'metricCards';
  /** Wait for scroll-into-view before showing the cards tip (avoids jump). */
  const [metricCardsTipReady, setMetricCardsTipReady] = useState(false);

  const syncReportsPhase = (phase: ReportsTourPhase) => {
    setReportsTourPhase(phase);
    if (showReportsTour) setTourPhase(phase);
  };

  const { selectedOrganization } = useOrganizationStore();
  const {
    monthly,
    overview,
    byBranch,
    isLoading,
    error,
    fetchEsgDashboard,
    reset,
  } = useEsgDashboardStore();
  const {
    report: carbonReport,
    isLoading: carbonLoading,
    error: carbonError,
    fetchCarbonAvoidanceReport,
    reset: resetCarbonReport,
  } = useCarbonAvoidanceReportStore();

  const [tab, setTab] = useState(0);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedSections, setSelectedSections] = useState<EsgReportSectionId[]>(
    () => loadEsgReportPreferences(),
  );
  const [pdfGeneratedLabel, setPdfGeneratedLabel] = useState(() =>
    format(new Date(), "MMM d, yyyy 'at' HH:mm")
  );
  const [carbonPdfGeneratedLabel, setCarbonPdfGeneratedLabel] = useState(() =>
    format(new Date(), "MMM d, yyyy 'at' HH:mm")
  );
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [organizationLogoSrc, setOrganizationLogoSrc] = useState<string | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const stored = sessionStorage.getItem('selectedMonth');
    if (stored) return stored;
    return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'");
  });

  const finishReportsGuide = () => {
    setReportsTourPhase('period');
    setHelpReportPhase('period');
    setTab(0);
    if (helpDownloadGuide) {
      setHelpDownloadTarget(null);
      clearHelpGuideActive();
      return;
    }
    if (helpMetricDefsGuide) {
      setHelpMetricDefsGuide(false);
      clearHelpGuideActive();
      if (showReportsTour) {
        completeStep('esgReports');
      }
      return;
    }
    completeStep('esgReports');
  };

  /** Soft-dismiss (X): Later for self-setup — do not mark the step done. */
  const softDismissReportsGuide = () => {
    if (helpDownloadGuide) {
      setHelpDownloadTarget(null);
      clearHelpGuideActive();
      setHelpReportPhase('period');
      setReportsTourPhase('period');
      return;
    }
    if (helpMetricDefsGuide) {
      setHelpMetricDefsGuide(false);
      clearHelpGuideActive();
      setReportsTourPhase('period');
      setTab(0);
      return;
    }
    dismissTour(reportsTourPhase);
  };

  // Resume self-setup tip from stored spot after Continue / re-login.
  useEffect(() => {
    if (!showReportsTour) return;
    if (tourPhase === 'nav') return;
    if (
      tourPhase === 'period' ||
      tourPhase === 'download' ||
      tourPhase === 'metricDefsTab' ||
      tourPhase === 'metricSearch' ||
      tourPhase === 'metricCards'
    ) {
      setReportsTourPhase(tourPhase);
      if (
        tourPhase === 'metricDefsTab' ||
        tourPhase === 'metricSearch' ||
        tourPhase === 'metricCards'
      ) {
        setTab(1);
      } else {
        setTab(0);
      }
    }
  }, [showReportsTour, tourPhase, setTourPhase]);

  const advanceReportsGuide = () => {
    if (helpDownloadGuide) {
      if (helpReportPhase === 'period') {
        setHelpReportPhase('download');
        return;
      }
      finishReportsGuide();
      return;
    }
    if (helpMetricDefsGuide) {
      if (reportsTourPhase === 'metricDefsTab') {
        setTab(1);
        setReportsTourPhase('metricSearch');
        return;
      }
      if (reportsTourPhase === 'metricSearch') {
        setReportsTourPhase('metricCards');
        return;
      }
      finishReportsGuide();
      return;
    }
    if (showPeriodTip) {
      syncReportsPhase('download');
      return;
    }
    if (showTourDownloadTip) {
      setTab(1);
      syncReportsPhase('metricDefsTab');
      return;
    }
    if (showMetricDefsTabTip) {
      setTab(1);
      syncReportsPhase('metricSearch');
      return;
    }
    if (showMetricSearchTip) {
      syncReportsPhase('metricCards');
      return;
    }
    finishReportsGuide();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (!guide) return;

    if (guide === HELP_GUIDE_METRIC_DEFINITIONS) {
      markHelpGuideActive(guide);
      setHelpMetricDefsGuide(true);
      setReportsTourPhase('metricDefsTab');
      setTab(1);
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }

    const target = parseHelpDownloadReportGuide(guide);
    if (!target) return;
    markHelpGuideActive(guide);
    setHelpDownloadTarget(target);
    setHelpReportPhase('period');
    setTab(0);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!showMetricCardsTip) {
      setMetricCardsTipReady(false);
      return;
    }
    setMetricCardsTipReady(false);
    let cancelled = false;
    const outer = window.requestAnimationFrame(() => {
      document
        .getElementById('self-setup-esg-reports-metric-cards')
        ?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!cancelled) setMetricCardsTipReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outer);
    };
  }, [showMetricCardsTip]);

  useEffect(() => {
    if (!activeReportsGuide) return;
    // Cards tip scrolls in its own settle effect so the tip never mounts mid-scroll.
    if (showMetricCardsTip) return;
    window.requestAnimationFrame(() => {
      const id = showMetricSearchTip
        ? 'self-setup-esg-reports-metric-search'
        : showMetricDefsTabTip
          ? 'self-setup-esg-reports-metric-tab'
          : showTourDownloadTip
            ? 'self-setup-esg-reports-download'
            : showHelpDownloadGeneral
              ? 'help-esg-reports-download-general'
              : showHelpDownloadWaste
                ? 'help-esg-reports-download-waste'
                : showHelpDownloadCarbon
                  ? 'help-esg-reports-download-carbon'
                  : showHelpAllDownloadTip
                    ? 'self-setup-esg-reports-download'
                    : 'self-setup-esg-reports-period';
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
  }, [
    activeReportsGuide,
    showTourDownloadTip,
    showHelpAllDownloadTip,
    showHelpDownloadGeneral,
    showHelpDownloadWaste,
    showHelpDownloadCarbon,
    showPeriodTip,
    showMetricDefsTabTip,
    showMetricSearchTip,
    showMetricCardsTip,
  ]);
  const selectedMonthDate = useMemo(() => parseDashboardMonth(selectedMonth), [selectedMonth]);

  const periodLabel = useMemo(
    () => format(selectedMonthDate, 'MMMM yyyy'),
    [selectedMonthDate]
  );

  const carbonPeriodLabel = useMemo(() => {
    const period = readCarbonReportPeriodFromSession();
    return carbonReportPeriodLabelFromReport(
      carbonReport,
      format(period.startDate, 'yyyy-MM-dd'),
      format(period.endDate, 'yyyy-MM-dd')
    );
  }, [carbonReport]);

  useEffect(() => {
    const logoUrl = selectedOrganization?.logo_url?.trim();
    if (!logoUrl) {
      setOrganizationLogoSrc(null);
      return;
    }
    let cancelled = false;
    fetchImageAsDataUrl(logoUrl).then((dataUrl) => {
      if (!cancelled) setOrganizationLogoSrc(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedOrganization?.logo_url]);

  const loadDashboardData = useCallback(() => {
    if (!selectedOrganization?.id || !selectedOrganization.organization_type) {
      reset();
      return;
    }
    fetchEsgDashboard(selectedOrganization.id, selectedOrganization.organization_type, selectedMonth);
  }, [selectedOrganization, selectedMonth, fetchEsgDashboard, reset]);

  const loadCarbonReport = useCallback(() => {
    const period = readCarbonReportPeriodFromSession();
    if (!selectedOrganization?.id || !isCarbonReportPeriodValid(period)) {
      resetCarbonReport();
      return;
    }
    fetchCarbonAvoidanceReport(
      selectedOrganization.id,
      carbonReportPeriodToParams(period)
    );
  }, [selectedOrganization, fetchCarbonAvoidanceReport, resetCarbonReport]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    loadCarbonReport();
  }, [loadCarbonReport]);

  const handleMonthChange = (newValue: Date | null) => {
    if (newValue && isValid(newValue)) {
      const start = startOfMonth(newValue);
      const iso = format(start, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      setSelectedMonth(iso);
      sessionStorage.setItem('selectedMonth', iso);
    }
  };

  const orgName = selectedOrganization?.name?.trim() || '';

  const sharedTemplateProps = {
    monthly,
    overview,
    byBranch,
    organizationName: orgName || 'Organization',
    organizationLogoSrc,
    periodLabel,
    generatedLabel: pdfGeneratedLabel,
    includeSections: selectedSections,
  };

  const runPdfDownload = async (variant: ReportPdfVariant) => {
    try {
      const stamp = format(new Date(), "MMM d, yyyy 'at' HH:mm");
      flushSync(() => {
        setPdfGeneratedLabel(stamp);
      });
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      const rootId =
        variant === 'general' ? 'esg-report-pdf-general' : 'esg-report-pdf-gri306';
      const el = document.getElementById(rootId);
      if (!el) {
        throw new Error('Report layout is not ready. Try again in a moment.');
      }

      const slug = sanitizeReportFileBaseName(selectedOrganization?.name ?? 'organization');
      const ym = format(selectedMonthDate, 'yyyy-MM');
      const prefix =
        variant === 'general' ? 'General-ESG-Report' : 'Waste-Circularity-GRI306-Report';
      const fileName = `${prefix}-${slug}-${ym}.pdf`;

      await downloadReportPdf(el, fileName);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'PDF export failed.');
    }
  };

  const runCarbonPdfDownload = async () => {
    try {
      const stamp = format(new Date(), "MMM d, yyyy 'at' HH:mm");
      flushSync(() => {
        setCarbonPdfGeneratedLabel(stamp);
      });
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      const el = document.getElementById('esg-report-pdf-carbon');
      if (!el) {
        throw new Error('Carbon report layout is not ready. Try again in a moment.');
      }

      const period = readCarbonReportPeriodFromSession();
      const slug = sanitizeReportFileBaseName(selectedOrganization?.name ?? 'organization');
      const startIso = format(period.startDate, 'yyyy-MM-dd');
      const endIso = format(period.endDate, 'yyyy-MM-dd');
      const fileName = `Carbon-Avoidance-Report-${slug}-${startIso}-${endIso}.pdf`;
      await downloadReportPdf(el, fileName);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'PDF export failed.');
    }
  };

  const disableDownloads = !selectedOrganization || isLoading;
  const disableCarbonDownload =
    !selectedOrganization ||
    carbonLoading ||
    !isCarbonReportPeriodValid(readCarbonReportPeriodFromSession()) ||
    !carbonReport;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <PageHeader
          icon={<AssessmentOutlinedIcon />}
          title={t('esgReports.title')}
          sx={{ mb: 2 }}
        >
          <Box sx={PAGE_HEADER_TABS_ROW_SX}>
            <Tabs
              id="self-setup-esg-reports-tabs"
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={PAGE_HEADER_TABS_SX}
            >
              <Tab label={t('esgReports.tabs.reports')} />
              <Tab
                id="self-setup-esg-reports-metric-tab"
                label={
                  showMetricDefsTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.esgReportsTabsTitle')}
                      subtitle={t('selfSetup.tour.esgReportsTabsSubtitle')}
                      onDismiss={softDismissReportsGuide}
                      onNext={advanceReportsGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box component="span" sx={{ display: 'inline-block' }}>
                        {t('esgReports.tabs.metricDefinitions')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('esgReports.tabs.metricDefinitions')
                  )
                }
              />
            </Tabs>
          </Box>
        </PageHeader>

        {tab === 0 && (
          <>
            <Paper
              elevation={2}
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: '16px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {t('esgReports.monthlyPeriod')}
                </Typography>
              <SetupCoachmark
                open={showPeriodTip}
                title={
                  helpDownloadGuide
                    ? tHelp('knowledgeBase.downloadReportPeriodTitle')
                    : t('selfSetup.tour.esgReportsPeriodTitle')
                }
                subtitle={
                  helpDownloadGuide
                    ? tHelp('knowledgeBase.downloadReportPeriodSubtitle')
                    : t('selfSetup.tour.esgReportsPeriodSubtitle')
                }
                onDismiss={softDismissReportsGuide}
                onNext={advanceReportsGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Box id="self-setup-esg-reports-period">
                  <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    value={selectedMonthDate}
                    maxDate={startOfMonth(new Date())}
                    onChange={handleMonthChange}
                    format="MMMM yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 220,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: theme.palette.background.default,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </SetupCoachmark>
              </Box>
              <Tooltip title={t('esgReports.customizeSubtitle')}>
                <Button
                  variant="outlined"
                  startIcon={<TuneOutlinedIcon />}
                  onClick={() => setCustomizeOpen(true)}
                  aria-label={t('esgReports.customize')}
                  sx={{
                    borderRadius: '12px',
                    px: 2.25,
                    py: 1,
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    gap: 0.75,
                  }}
                >
                  {t('esgReports.customize')}
                  {selectedSections.length < ALL_ESG_REPORT_SECTIONS.length && (
                    <Chip
                      component="span"
                      label={selectedSections.length}
                      size="small"
                      sx={{
                        height: 22,
                        '& .MuiChip-label': {
                          px: 1,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        },
                      }}
                    />
                  )}
                </Button>
              </Tooltip>
            </Paper>

            <EsgReportCustomizeDialog
              open={customizeOpen}
              selectedSections={selectedSections}
              onClose={() => setCustomizeOpen(false)}
              onSave={(next) => {
                setSelectedSections(next);
                saveEsgReportPreferences(next);
                setCustomizeOpen(false);
              }}
            />

            {!selectedOrganization && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('esgReports.selectOrganization')}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {carbonError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {carbonError}
              </Alert>
            )}

            {(isLoading || carbonLoading) && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('esgReports.chooseReportHint')}
            </Typography>

            <SetupCoachmark
              open={showGridDownloadTip}
              title={
                showHelpAllDownloadTip
                  ? tHelp('knowledgeBase.downloadReportButtonTitle')
                  : t('selfSetup.tour.esgReportsTitle')
              }
              subtitle={
                showHelpAllDownloadTip
                  ? tHelp('knowledgeBase.downloadReportAllButtonSubtitle')
                  : t('selfSetup.tour.esgReportsSubtitle')
              }
              onDismiss={softDismissReportsGuide}
              onNext={advanceReportsGuide}
              nextLabel={
                showHelpAllDownloadTip
                  ? t('selfSetup.tourDone')
                  : t('selfSetup.tourNext')
              }
              fullWidth
            >
              <Grid
                id="self-setup-esg-reports-download"
                container
                spacing={3}
                alignItems="stretch"
                sx={{ mb: 4 }}
              >
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: '16px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                      <AssessmentOutlinedIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('esgReports.cards.general.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {periodLabel}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 'auto' }} id="help-esg-reports-download-general">
                      <SetupCoachmark
                        open={showHelpDownloadGeneral}
                        title={tHelp('knowledgeBase.downloadReportButtonTitle')}
                        subtitle={tHelp(
                          'knowledgeBase.downloadReportButtonSubtitle',
                        )}
                        onDismiss={softDismissReportsGuide}
                        onNext={advanceReportsGuide}
                        nextLabel={t('selfSetup.tourDone')}
                        fullWidth
                      >
                        <ReportDownloadPdfButton
                          disabled={disableDownloads}
                          onDownload={() => runPdfDownload('general')}
                        />
                      </SetupCoachmark>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: '16px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                      <RecyclingOutlinedIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {t('esgReports.cards.gri306.title')}
                          <Typography
                            component="span"
                            display="block"
                            variant="body2"
                            sx={{ fontWeight: 500, color: 'text.secondary', mt: 0.25 }}
                          >
                            {t('esgReports.cards.gri306.badge')}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {periodLabel}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 'auto' }} id="help-esg-reports-download-waste">
                      <SetupCoachmark
                        open={showHelpDownloadWaste}
                        title={tHelp('knowledgeBase.downloadReportButtonTitle')}
                        subtitle={tHelp(
                          'knowledgeBase.downloadReportButtonSubtitle',
                        )}
                        onDismiss={softDismissReportsGuide}
                        onNext={advanceReportsGuide}
                        nextLabel={t('selfSetup.tourDone')}
                        fullWidth
                      >
                        <ReportDownloadPdfButton
                          disabled={disableDownloads}
                          onDownload={() => runPdfDownload('waste_circularity_gri306')}
                        />
                      </SetupCoachmark>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: '16px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                      <Co2OutlinedIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t('esgReports.cards.carbon.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {carbonPeriodLabel}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 'auto' }} id="help-esg-reports-download-carbon">
                      <SetupCoachmark
                        open={showHelpDownloadCarbon}
                        title={tHelp('knowledgeBase.downloadReportButtonTitle')}
                        subtitle={tHelp(
                          'knowledgeBase.downloadReportButtonSubtitle',
                        )}
                        onDismiss={softDismissReportsGuide}
                        onNext={advanceReportsGuide}
                        nextLabel={t('selfSetup.tourDone')}
                        fullWidth
                      >
                        <ReportDownloadPdfButton
                          disabled={disableCarbonDownload}
                          onDownload={runCarbonPdfDownload}
                        />
                      </SetupCoachmark>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              </Grid>
            </SetupCoachmark>
          </>
        )}

        {tab === 1 && (
          <ReportMetricDefinitionsTab
            tourTip={
              showMetricSearchTip
                ? 'metricSearch'
                : showMetricCardsTip && metricCardsTipReady
                  ? 'metricCards'
                  : null
            }
            onTourNext={advanceReportsGuide}
            onTourDismiss={softDismissReportsGuide}
            tourNextLabel={
              showMetricCardsTip
                ? t('selfSetup.tourDone')
                : t('selfSetup.tourNext')
            }
          />
        )}

        <Box
          sx={{
            position: 'fixed',
            left: -16000,
            top: 0,
            width: 794,
            pointerEvents: 'none',
            zIndex: -9999,
          }}
          aria-hidden
        >
          <ReportPdfTemplate
            containerId="esg-report-pdf-general"
            variant="general"
            {...sharedTemplateProps}
          />
          <ReportPdfTemplate
            containerId="esg-report-pdf-gri306"
            variant="waste_circularity_gri306"
            {...sharedTemplateProps}
          />
          <CarbonAvoidanceReportPdfTemplate
            containerId="esg-report-pdf-carbon"
            report={carbonReport}
            organizationName={orgName || 'Organization'}
            organizationLogoSrc={organizationLogoSrc}
            periodLabel={carbonPeriodLabel}
            generatedLabel={carbonPdfGeneratedLabel}
            includeSections={selectedSections}
          />
        </Box>

        <Snackbar
          open={pdfError !== null}
          autoHideDuration={6000}
          onClose={() => setPdfError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setPdfError(null)} sx={{ width: '100%' }}>
            {pdfError}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
